import {
  type ClientLogEntry,
  type ClientLogTransport,
  PermanentTransportError,
} from './client-logger';

export interface HttpTransportOptions {
  /** HTTP endpoint URL to POST log entries to */
  endpoint: string;
}

/**
 * Transport that sends log entries via HTTP POST.
 * Used by famfam which uses Next.js without a custom WebSocket server.
 *
 * 4xx responses (other than 408 Request Timeout and 429 Too Many Requests)
 * are treated as permanent — `ClientLogger` will drop the entries instead
 * of re-queueing them, and the failure does not escalate flush backoff.
 * 5xx and network errors stay re-queueable and do escalate backoff.
 */
export class HttpTransport implements ClientLogTransport {
  private endpoint: string;

  /** Initialize the transport targeting the given HTTP endpoint URL. */
  constructor(options: HttpTransportOptions) {
    this.endpoint = options.endpoint;
  }

  /** POST the entries as JSON; throws `PermanentTransportError` for non-retryable 4xx responses. */
  async send(entries: readonly ClientLogEntry[]): Promise<void> {
    const response = await fetch(this.endpoint, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ entries }),
    });
    if (!response.ok) {
      const message = `HttpTransport send failed: ${response.status} ${response.statusText}`;
      if (
        response.status >= 400 &&
        response.status < 500 &&
        response.status !== 408 &&
        response.status !== 429
      ) {
        throw new PermanentTransportError(message, response.status);
      }
      throw new Error(message);
    }
  }
}
